import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { schoolService } from '../../services';
import {
  getBulkSchoolTemplateUrl,
  BULK_SCHOOL_TEMPLATE_DOWNLOAD_NAME,
} from '../../constants/bulkSchoolTemplate';
import { bulkStatusLabel } from '../../constants/bulkSchoolStatus';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', md: 800 },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
};

const statusLabel = bulkStatusLabel;

const STATUS_COLOR = {
  Added: 'success',
  Updated: 'success',
  Duplicate: 'warning',
  NoMatch: 'warning',
  Invalid: 'error',
  Failed: 'error',
  Pending: 'default',
};

// In the dry-run preview nothing is persisted yet, so the chips read as intentions.
const DRY_RUN_LABELS = {
  Added: 'Will add',
  Updated: 'Will update',
  Duplicate: 'Duplicate',
  Invalid: 'Invalid',
  NoMatch: 'No match',
  Failed: 'Failed',
  Pending: 'Pending',
};

const isSaveWorthy = (data, mode) =>
  mode === 'Update' ? (data.updatedCount || 0) > 0 : (data.addedCount || 0) > 0;

function ResultTable({ data, dryRun }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Row</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>URN</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Details</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.rows.map((row) => {
          const label = statusLabel(row.status);
          return (
            <TableRow key={row.rowNumber}>
              <TableCell>{row.rowNumber}</TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.urn || '-'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={STATUS_COLOR[label] || 'default'}
                  label={dryRun ? (DRY_RUN_LABELS[label] || label) : label}
                />
              </TableCell>
              <TableCell>{(row.errors || []).join(' ')}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function CountChips({ data, mode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
      <Chip label={`Total: ${data.totalRows}`} />
      {mode === 'Add' && <Chip color="success" label={`Added: ${data.addedCount}`} />}
      {mode === 'Update' && <Chip color="success" label={`Updated: ${data.updatedCount || 0}`} />}
      {mode === 'Update' && <Chip label={`No match: ${data.noMatchCount || 0}`} />}
      <Chip color="warning" label={`Duplicates: ${data.duplicateCount}`} />
      <Chip color="error" label={`Invalid: ${data.invalidCount}`} />
      <Chip color="error" label={`Failed: ${data.failedCount}`} />
    </Box>
  );
}

export default function BulkAddSchoolsModal({ open, onClose, onCompleted, matId, groupName }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('Add');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setMode('Add');
    setBusy(false);
    setPreview(null);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const chooseFile = (selected) => {
    setFile(selected);
    setPreview(null);
    setResult(null);
  };

  const changeMode = (next) => {
    if (!next) return;
    setMode(next);
    setPreview(null);
    setResult(null);
  };

  const runUpload = async (dryRun) => {
    if (!file) {
      toast.error('Please choose an .xlsx file to upload.');
      return null;
    }
    setBusy(true);
    try {
      const { success, message, result } = await schoolService.bulkAddSchools(file, {
        matGroupId: matId,
        mode,
        dryRun,
      });
      if (success && result) {
        return result;
      }
      toast.error(message || 'The upload could not be processed.');
      return null;
    } catch (err) {
      // The service rejects with the parsed API error body ({ success:false, message }); its
      // message is the server's real reason (bad file, dead group, …). Anything without that
      // shape is a programming error whose message would only confuse the operator.
      toast.error((err?.success === false && err.message) || 'Failed to upload the file.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    const data = await runUpload(true);
    if (data) {
      setResult(null);
      setPreview(data);
    }
  };

  const handleConfirm = async () => {
    const data = await runUpload(false);
    if (data) {
      setResult(data);
      setPreview(null);
      if (isSaveWorthy(data, mode) && onCompleted) {
        onCompleted();
      }
      const saved = mode === 'Update' ? (data.updatedCount || 0) : data.addedCount;
      if (saved > 0) {
        toast.success(`${saved} school(s) ${mode === 'Update' ? 'updated' : 'added'}.`);
      } else {
        // Nothing persisted — every row was a duplicate, invalid or failed. A success toast
        // here would contradict the row table the operator is about to read.
        toast.warning('No schools were saved — check the row details below.');
      }
    }
  };

  const helperText =
    mode === 'Update'
      ? 'Rows are matched by URN, then by school name. Blank cells leave the existing value unchanged.'
      : 'Each row is validated and rejected if it duplicates an existing school (by URN/GIAS ID or name) or an earlier row in the file.';

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">Bulk Add Schools</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {matId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Schools will be added to {groupName || 'this group'}.
          </Alert>
        )}

        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_e, next) => changeMode(next)}
          sx={{ mb: 1.5 }}
        >
          <ToggleButton value="Add">Add new schools</ToggleButton>
          <ToggleButton value="Update">Update existing schools</ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload an .xlsx workbook with a header row. {helperText} Download the template below
          for the expected columns.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Button
            variant="text"
            startIcon={<DownloadIcon />}
            component="a"
            href={getBulkSchoolTemplateUrl()}
            download={BULK_SCHOOL_TEMPLATE_DOWNLOAD_NAME}
          >
            Download template
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" component="label">
            {file ? file.name : 'Choose file'}
            <input
              type="file"
              hidden
              accept=".xlsx"
              onChange={(e) => chooseFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Button variant="outlined" onClick={handlePreview} disabled={busy || !file}>
            {busy && !result ? <CircularProgress size={20} /> : 'Preview'}
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={busy || !preview}>
            Confirm
          </Button>
        </Box>

        {preview && (
          <Box>
            <CountChips data={preview} mode={mode} />
            <Alert severity="info" sx={{ mb: 1 }}>
              This is a preview — nothing saved yet. Review the rows below, then Confirm to apply.
            </Alert>
            <ResultTable data={preview} dryRun />
          </Box>
        )}

        {result && (
          <Box>
            <CountChips data={result} mode={mode} />
            <ResultTable data={result} dryRun={false} />
          </Box>
        )}
      </Box>
    </Modal>
  );
}
